import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {ShieldCheck, Mail, Lock, User, ArrowLeft, Eye, EyeOff, KeyRound, CheckCircle2, X} from 'lucide-react'
import { lumi } from '@/lib/lumi'

const C = {
  bgDeep:     '#000000',
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.10)',
  turqBright: '#3ce8d6',
  borderTurq: 'rgba(26,214,196,0.28)',
  border:     'rgba(255,255,255,0.08)',
  borderMed:  'rgba(255,255,255,0.14)',
  text:       '#9aa7b4',
  textMuted:  '#5a6672',
  error:      '#f87171',
  errorDim:   'rgba(248,113,113,0.10)',
}

const LOGO = 'https://static.lumi.new/9c/9c1ccefd81b9dbb855ed621cfae893f3.webp'

type Mode = 'signin' | 'signup' | 'otp' | 'forgot' | 'reset_otp' | 'reset_pass' | 'done'

const inputBase: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${C.borderMed}`,
  color: '#ffffff',
  width: '100%',
  borderRadius: 10,
  padding: '12px 14px',
  fontSize: 16,
  outline: 'none',
}

// ────────────────────────────────────────────────
// Module-scope components (stable identity across renders).
// Defining these here — NOT inside Auth() — prevents React from
// unmounting/remounting inputs on every keystroke, which on mobile
// caused the keyboard to close after each character.
// ────────────────────────────────────────────────

function InputIcon({ icon: Icon }: { icon: React.ElementType }) {
  return (
    <Icon
      className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
      style={{ color: C.turq }}
    />
  )
}

function CardWrap({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-16 relative"
      style={{ background: C.bgDeep }}
    >
      {/* Top glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(26,214,196,0.09) 0%, transparent 70%)' }}
      />

      {/* Close / back to site button — visible on all screen sizes */}
      <button
        onClick={onClose}
        aria-label="Close and go back"
        className="fixed top-4 right-4 z-50 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:bg-white/10 active:scale-95"
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${C.borderMed}`,
          color: C.text,
          backdropFilter: 'blur(10px)',
        }}
      >
        <X className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Back to site</span>
      </button>

      <div className="w-full max-w-[420px] relative z-10 flex flex-col items-center">
        {/* Logo */}
        <Link to="/">
          <img src={LOGO} alt="Crypto Audit Service" className="h-12 w-auto object-contain mb-8" draggable={false} />
        </Link>

        {/* Card */}
        <div
          className="w-full rounded-2xl p-8"
          style={{
            background: '#080c0f',
            border: `1px solid ${C.border}`,
            boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          }}
        >
          {children}
        </div>

        <p className="mt-6 text-xs text-center" style={{ color: C.textMuted }}>
          By continuing you agree to the{' '}
          <span style={{ color: C.turq }} className="cursor-pointer hover:underline">Terms of Service</span>
          {' '}and{' '}
          <span style={{ color: C.turq }} className="cursor-pointer hover:underline">Privacy Policy</span>.
        </p>
      </div>
    </div>
  )
}

function ErrorBox({ error }: { error: string }) {
  if (!error) return null
  return (
    <div
      className="rounded-lg px-4 py-3 text-sm mb-5"
      style={{ background: C.errorDim, border: `1px solid rgba(248,113,113,0.2)`, color: C.error }}
    >
      {error}
    </div>
  )
}

function InfoBox({ info }: { info: string }) {
  if (!info) return null
  return (
    <div
      className="rounded-lg px-4 py-3 text-sm mb-5"
      style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}`, color: C.turq }}
    >
      {info}
    </div>
  )
}

function SubmitBtn({ label, loading }: { label: string; loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
      style={{ background: C.turq, color: '#000000' }}
    >
      {loading ? 'Please wait…' : label}
    </button>
  )
}

export default function Auth() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('signin')

  // Shared fields
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [otp,      setOtp]      = useState('')
  const [newPass,  setNewPass]  = useState('')
  const [showPass, setShowPass] = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [info,     setInfo]     = useState('')

  function clearMessages() { setError(''); setInfo('') }
  function handleClose() { navigate(-1) }

  // ── Sign In ──
  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)
    try {
      await lumi.auth.signInWithPassword({ email, password })
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Sign in failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  // ── Sign Up: step 1 — send OTP ──
  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)
    try {
      await lumi.auth.sendOtp({ email })
      setInfo(`Verification code sent to ${email}`)
      setMode('otp')
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Could not send verification code. Try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Sign Up: step 2 — create account with OTP ──
  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)
    try {
      await lumi.auth.signUpWithPassword({ email, password, otp, userName: username })
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Registration failed. Please check the code and try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Forgot Password: send OTP ──
  async function handleForgotOtp(e: React.FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)
    try {
      await lumi.auth.sendOtp({ email })
      setInfo(`Reset code sent to ${email}`)
      setMode('reset_otp')
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Could not send reset code. Try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Forgot Password: reset ──
  async function handleResetPass(e: React.FormEvent) {
    e.preventDefault()
    clearMessages()
    setLoading(true)
    try {
      await lumi.auth.resetPassword({ email, otp, newPassword: newPass })
      setMode('done')
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Password reset failed. Please check the code and try again.')
    } finally {
      setLoading(false)
    }
  }

  // ════════════════════════════════════════════
  // SIGN IN
  // ════════════════════════════════════════════
  if (mode === 'signin') {
    return (
      <CardWrap onClose={handleClose}>
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-5 h-5" style={{ color: C.turq }} />
            <h1 className="text-xl font-semibold text-white">Sign In</h1>
          </div>
          <p className="text-sm" style={{ color: C.textMuted }}>Welcome back to Crypto Audit Service.</p>
        </div>

        <ErrorBox error={error} />

        <form onSubmit={handleSignIn} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Email</label>
            <div className="relative">
              <InputIcon icon={Mail} />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{ ...inputBase, paddingLeft: 40 }}
              />
            </div>
            <p className="text-xs" style={{ color: C.textMuted }}>
              Sign in with your email address, not your username.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Password</label>
            <div className="relative">
              <InputIcon icon={Lock} />
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{ ...inputBase, paddingLeft: 40, paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2"
                style={{ color: C.textMuted }}
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end -mt-1">
            <button
              type="button"
              onClick={() => { clearMessages(); setMode('forgot') }}
              className="text-xs transition-colors hover:text-white"
              style={{ color: C.turq }}
            >
              Forgot password?
            </button>
          </div>

          <SubmitBtn label="Sign In" loading={loading} />
        </form>

        <div className="mt-6 text-center text-sm" style={{ color: C.textMuted }}>
          Don't have an account?{' '}
          <button
            onClick={() => { clearMessages(); setMode('signup') }}
            className="font-medium transition-colors hover:text-white"
            style={{ color: C.turq }}
          >
            Create one
          </button>
        </div>
      </CardWrap>
    )
  }

  // ════════════════════════════════════════════
  // SIGN UP — step 1
  // ════════════════════════════════════════════
  if (mode === 'signup') {
    return (
      <CardWrap onClose={handleClose}>
        <div className="mb-7">
          <button
            onClick={() => { clearMessages(); setMode('signin') }}
            className="flex items-center gap-1.5 text-xs mb-4 transition-colors hover:text-white"
            style={{ color: C.textMuted }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-5 h-5" style={{ color: C.turq }} />
            <h1 className="text-xl font-semibold text-white">Create Account</h1>
          </div>
          <p className="text-sm" style={{ color: C.textMuted }}>Get your project audited by Crypto Audit Service.</p>
        </div>

        <ErrorBox error={error} />

        <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Username</label>
            <div className="relative">
              <InputIcon icon={User} />
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="yourname"
                required
                style={{ ...inputBase, paddingLeft: 40 }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Email</label>
            <div className="relative">
              <InputIcon icon={Mail} />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{ ...inputBase, paddingLeft: 40 }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Password</label>
            <div className="relative">
              <InputIcon icon={Lock} />
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                required
                minLength={8}
                style={{ ...inputBase, paddingLeft: 40, paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2"
                style={{ color: C.textMuted }}
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <SubmitBtn label="Send Verification Code" loading={loading} />
        </form>

        <div className="mt-6 text-center text-sm" style={{ color: C.textMuted }}>
          Already have an account?{' '}
          <button
            onClick={() => { clearMessages(); setMode('signin') }}
            className="font-medium transition-colors hover:text-white"
            style={{ color: C.turq }}
          >
            Sign In
          </button>
        </div>
      </CardWrap>
    )
  }

  // ════════════════════════════════════════════
  // SIGN UP — step 2 (OTP)
  // ════════════════════════════════════════════
  if (mode === 'otp') {
    return (
      <CardWrap onClose={handleClose}>
        <div className="mb-7">
          <button
            onClick={() => { clearMessages(); setMode('signup') }}
            className="flex items-center gap-1.5 text-xs mb-4 transition-colors hover:text-white"
            style={{ color: C.textMuted }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <KeyRound className="w-5 h-5" style={{ color: C.turq }} />
            <h1 className="text-xl font-semibold text-white">Verify Email</h1>
          </div>
          <p className="text-sm" style={{ color: C.textMuted }}>
            Enter the 6-digit code sent to <span className="text-white">{email}</span>.
          </p>
        </div>

        <InfoBox info={info} />
        <ErrorBox error={error} />

        <form onSubmit={handleSignUp} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Verification Code</label>
            <div className="relative">
              <InputIcon icon={KeyRound} />
              <input
                type="text"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                maxLength={6}
                style={{ ...inputBase, paddingLeft: 40, letterSpacing: '0.2em', fontFamily: 'monospace', textAlign: 'center' }}
              />
            </div>
          </div>

          <SubmitBtn label="Create Account" loading={loading} />

          <button
            type="button"
            onClick={handleSendOtp}
            disabled={loading}
            className="text-xs text-center transition-colors hover:text-white"
            style={{ color: C.textMuted }}
          >
            Didn't receive it? <span style={{ color: C.turq }}>Resend code</span>
          </button>
        </form>
      </CardWrap>
    )
  }

  // ════════════════════════════════════════════
  // FORGOT PASSWORD
  // ════════════════════════════════════════════
  if (mode === 'forgot') {
    return (
      <CardWrap onClose={handleClose}>
        <div className="mb-7">
          <button
            onClick={() => { clearMessages(); setMode('signin') }}
            className="flex items-center gap-1.5 text-xs mb-4 transition-colors hover:text-white"
            style={{ color: C.textMuted }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <Lock className="w-5 h-5" style={{ color: C.turq }} />
            <h1 className="text-xl font-semibold text-white">Reset Password</h1>
          </div>
          <p className="text-sm" style={{ color: C.textMuted }}>
            Enter your email and we'll send a reset code.
          </p>
        </div>

        <ErrorBox error={error} />

        <form onSubmit={handleForgotOtp} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Email</label>
            <div className="relative">
              <InputIcon icon={Mail} />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{ ...inputBase, paddingLeft: 40 }}
              />
            </div>
          </div>
          <SubmitBtn label="Send Reset Code" loading={loading} />
        </form>
      </CardWrap>
    )
  }

  // ════════════════════════════════════════════
  // RESET — enter OTP + new password
  // ════════════════════════════════════════════
  if (mode === 'reset_otp') {
    return (
      <CardWrap onClose={handleClose}>
        <div className="mb-7">
          <div className="flex items-center gap-2 mb-1.5">
            <KeyRound className="w-5 h-5" style={{ color: C.turq }} />
            <h1 className="text-xl font-semibold text-white">Set New Password</h1>
          </div>
          <p className="text-sm" style={{ color: C.textMuted }}>
            Enter the code sent to <span className="text-white">{email}</span> and choose a new password.
          </p>
        </div>

        <InfoBox info={info} />
        <ErrorBox error={error} />

        <form onSubmit={handleResetPass} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>Reset Code</label>
            <div className="relative">
              <InputIcon icon={KeyRound} />
              <input
                type="text"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                maxLength={6}
                style={{ ...inputBase, paddingLeft: 40, letterSpacing: '0.2em', fontFamily: 'monospace', textAlign: 'center' }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: C.text }}>New Password</label>
            <div className="relative">
              <InputIcon icon={Lock} />
              <input
                type={showPass ? 'text' : 'password'}
                value={newPass}
                onChange={e => setNewPass(e.target.value)}
                placeholder="Min. 8 characters"
                required
                minLength={8}
                style={{ ...inputBase, paddingLeft: 40, paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2"
                style={{ color: C.textMuted }}
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <SubmitBtn label="Reset Password" loading={loading} />
        </form>
      </CardWrap>
    )
  }

  // ════════════════════════════════════════════
  // DONE — reset success
  // ════════════════════════════════════════════
  if (mode === 'done') {
    return (
      <CardWrap onClose={handleClose}>
        <div className="flex flex-col items-center text-center gap-5 py-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
          >
            <CheckCircle2 className="w-8 h-8" style={{ color: C.turq }} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white mb-2">Password Reset!</h1>
            <p className="text-sm" style={{ color: C.textMuted }}>
              Your password has been updated. You can now sign in with your new credentials.
            </p>
          </div>
          <button
            onClick={() => { clearMessages(); setMode('signin') }}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110"
            style={{ background: C.turq, color: '#000000' }}
          >
            Back to Sign In
          </button>
        </div>
      </CardWrap>
    )
  }

  return null
}
